/**
 * SC Revision Delta — what changed between two Chapter 99 revisions.
 *
 * Companion to the SC Node Visualizer: that page shows one revision's text,
 * this one shows the movement between two. Everything here is derived live
 * from the node x revision -> content cross-reference; no tables were added
 * and the visualizer is untouched.
 */
import React, { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import TopBar from '../../../../../components/utils/TopBar';
import * as api from '../../../../../api/index';
import { toast } from 'react-toastify';

const C = {
  bg: '#0f1117', panel: '#1a1b23', sunk: '#121620', line: '#1e293b',
  border: '#334155', text: '#e2e8f0', dim: '#94a3b8', muted: '#64748b',
  blue: '#60a5fa',
  added: '#4ade80', addedBg: '#132a1e',
  removed: '#f87171', removedBg: '#2a1515',
  changed: '#fbbf24', changedBg: '#2a2213',
  same: '#64748b',
};

const KIND = {
  ADDED:   { color: C.added,   bg: C.addedBg,   label: 'Added' },
  REMOVED: { color: C.removed, bg: C.removedBg, label: 'Removed' },
  CHANGED: { color: C.changed, bg: C.changedBg, label: 'Changed' },
};

const inputSx = {
  '& .MuiInputBase-root': { color: C.text, background: C.sunk, fontSize: 13 },
  '& .MuiInputLabel-root': { color: C.muted, fontSize: 13 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
};

const Panel = ({ title, right, children, pad = '16px' }) => (
  <Box sx={{ background: C.panel, border: `1px solid ${C.line}`, borderRadius: '8px', overflow: 'hidden', minWidth: 0 }}>
    <Box sx={{
      px: '14px', py: '9px', background: C.bg, borderBottom: `1px solid ${C.line}`,
      display: 'flex', alignItems: 'center', gap: '8px',
    }}>
      <Typography variant="caption" fontWeight={700} color={C.dim}
        sx={{ letterSpacing: '.06em', textTransform: 'uppercase', fontSize: 10 }}>
        {title}
      </Typography>
      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>{right}</Box>
    </Box>
    <Box sx={{ p: pad }}>{children}</Box>
  </Box>
);

/** Proportional bar: added / changed / removed / unchanged as one strip. */
const ChurnBar = ({ added, changed, removed, unchanged, height = 8 }) => {
  const total = added + changed + removed + unchanged;
  if (!total) return null;
  const seg = (n, color) => n > 0 && (
    <Box sx={{ width: `${(n / total) * 100}%`, background: color, height: '100%' }} />
  );
  return (
    <Box sx={{ display: 'flex', height, borderRadius: '99px', overflow: 'hidden', background: '#0b0e14' }}>
      {seg(added, C.added)}
      {seg(changed, C.changed)}
      {seg(removed, C.removed)}
      {seg(unchanged, '#2a3442')}
    </Box>
  );
};

const ScRevisionDeltaPage = () => {
  const [loading, setLoading] = useState(true);
  const [diffing, setDiffing] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [fromRev, setFromRev] = useState('');
  const [toRev, setToRev] = useState('');
  const [diff, setDiff] = useState(null);
  const [error, setError] = useState(null);

  const [kindFilter, setKindFilter] = useState('ALL');
  const [scFilter, setScFilter] = useState('ALL');

  const [nodeDiff, setNodeDiff] = useState(null);
  const [nodeDiffLoading, setNodeDiffLoading] = useState(false);

  useEffect(() => { loadTimeline(); }, []); // eslint-disable-line

  const loadTimeline = async () => {
    setLoading(true);
    try {
      const { data, status } = await api.getScRevisionTimelineBIZcoreTD();
      if (status === 200 && data?.data) {
        const revs = data.data.revisions || [];
        setTimeline(revs);
        // Default to the most recent pair — the comparison people want first.
        if (revs.length >= 2) {
          setFromRev(revs[revs.length - 2].revision_id);
          setToRev(revs[revs.length - 1].revision_id);
        }
      }
    } catch (err) {
      toast.error('Could not load the revision timeline');
    }
    setLoading(false);
  };

  // Run the comparison whenever a valid, distinct pair is selected.
  useEffect(() => {
    if (!fromRev || !toRev || fromRev === toRev) { setDiff(null); return; }
    let cancelled = false;
    (async () => {
      setDiffing(true); setError(null);
      try {
        const { data, status } = await api.getScRevisionDiffBIZcoreTD(fromRev, toRev);
        if (cancelled) return;
        if (status === 200 && data?.data) { setDiff(data.data); setKindFilter('ALL'); setScFilter('ALL'); }
        else setError(typeof data?.data === 'string' ? data.data : 'Could not compute the diff');
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.data || 'Could not compute the diff');
      }
      if (!cancelled) setDiffing(false);
    })();
    return () => { cancelled = true; };
  }, [fromRev, toRev]);

  const openNodeDiff = async (node) => {
    setNodeDiff({ loading: true, path: node.node_label_path });
    setNodeDiffLoading(true);
    try {
      const { data, status } = await api.getScNodeDiffBIZcoreTD(node.node_id, fromRev, toRev);
      if (status === 200 && data?.data) setNodeDiff({ ...data.data, kind: node.kind });
      else { toast.error('Could not load the node diff'); setNodeDiff(null); }
    } catch (err) {
      toast.error('Could not load the node diff');
      setNodeDiff(null);
    }
    setNodeDiffLoading(false);
  };

  const swap = () => { const a = fromRev; setFromRev(toRev); setToRev(a); };

  const visibleNodes = useMemo(() => {
    let rows = diff?.nodes || [];
    if (kindFilter !== 'ALL') rows = rows.filter((n) => n.kind === kindFilter);
    if (scFilter !== 'ALL') rows = rows.filter((n) => n.subchapter === scFilter);
    return rows;
  }, [diff, kindFilter, scFilter]);

  const maxTimelineChurn = Math.max(1, ...timeline.map((t) => t.churn));

  if (loading) {
    return (
      <Box>
        <TopBar title={"SC Revision Delta"} />
        <Box height="300px" className="flexCenterCenterRow"><CircularProgress /></Box>
      </Box>
    );
  }

  if (timeline.length < 2) {
    return (
      <Box>
        <TopBar title={"SC Revision Delta"} />
        <Alert severity="info" sx={{ mt: '16px' }}>
          Two ingested revisions are needed to compare. {timeline.length === 1
            ? `Only "${timeline[0].revision_name}" is loaded — ingest another Chapter 99 PDF to see a delta.`
            : 'No revisions are loaded yet.'}
        </Alert>
      </Box>
    );
  }

  const s = diff?.summary;

  return (
    <Box>
      <TopBar title={"SC Revision Delta"} />

      <Box sx={{ background: C.bg, borderRadius: '8px', border: `1px solid ${C.line}`, p: '16px' }}>

        {/* ── Publication timeline ─────────────────────────────────────── */}
        <Panel title="Publication timeline" right={
          <Typography variant="caption" color={C.muted} sx={{ fontSize: 10.5 }}>
            churn measured at ingest, against the preceding revision
          </Typography>
        }>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '10px', overflowX: 'auto', pb: '4px' }}>
            {timeline.map((t) => {
              const isFrom = t.revision_id === fromRev;
              const isTo = t.revision_id === toRev;
              const active = isFrom || isTo;
              return (
                <Box key={t.revision_id}
                  onClick={() => { if (!isFrom) { setFromRev(toRev); setToRev(t.revision_id); } }}
                  sx={{
                    minWidth: 104, cursor: 'pointer', flexShrink: 0,
                    p: '8px 10px', borderRadius: '6px',
                    background: active ? '#16202c' : 'transparent',
                    border: `1px solid ${active ? C.blue : C.line}`,
                    '&:hover': { borderColor: C.blue },
                  }}>
                  <Box sx={{ height: 54, display: 'flex', alignItems: 'flex-end', mb: '6px' }}>
                    <Box sx={{
                      width: '100%',
                      height: `${Math.max(3, (t.churn / maxTimelineChurn) * 54)}px`,
                      background: t.churn === 0 ? '#2a3442' : C.changed,
                      borderRadius: '3px 3px 0 0',
                    }} />
                  </Box>
                  <Typography variant="caption" color={C.text} sx={{ fontSize: 11, fontWeight: 600, display: 'block' }}>
                    {t.revision_name}
                  </Typography>
                  <Typography variant="caption" color={C.muted} sx={{ fontSize: 10, display: 'block' }}>
                    {t.effective_date}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: 10, color: t.churn ? C.changed : C.muted }}>
                    {t.churn} changed · {t.churn_pct}%
                  </Typography>
                  {active && (
                    <Chip label={isFrom ? 'FROM' : 'TO'} size="small" sx={{
                      mt: '5px', height: 15, fontSize: 8.5, fontWeight: 700,
                      background: 'transparent', color: C.blue, border: `1px solid ${C.blue}`,
                      '& .MuiChip-label': { px: '5px' },
                    }} />
                  )}
                </Box>
              );
            })}
          </Box>
        </Panel>

        {/* ── Pair selector ────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', my: '14px', flexWrap: 'wrap' }}>
          <TextField select size="small" label="From" value={fromRev}
            onChange={(e) => setFromRev(e.target.value)} sx={{ ...inputSx, minWidth: 220 }}>
            {timeline.map((t) => (
              <MenuItem key={t.revision_id} value={t.revision_id} sx={{ fontSize: 13 }}>
                {t.revision_name} · {t.effective_date}
              </MenuItem>
            ))}
          </TextField>

          <Tooltip title="Swap direction">
            <IconButton onClick={swap} sx={{ color: C.blue, border: `1px solid ${C.border}`, borderRadius: '6px' }}>
              <SwapHorizOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <TextField select size="small" label="To" value={toRev}
            onChange={(e) => setToRev(e.target.value)} sx={{ ...inputSx, minWidth: 220 }}>
            {timeline.map((t) => (
              <MenuItem key={t.revision_id} value={t.revision_id} sx={{ fontSize: 13 }}>
                {t.revision_name} · {t.effective_date}
              </MenuItem>
            ))}
          </TextField>

          {diffing && <CircularProgress size={18} sx={{ color: C.blue }} />}
        </Box>

        {fromRev === toRev && (
          <Alert severity="info" sx={{ mb: '14px' }}>Pick two different revisions to compare.</Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: '14px' }}>{String(error)}</Alert>}

        {diff && s && (
          <>
            {/* ── Summary ──────────────────────────────────────────────── */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', mb: '14px' }}>
              {[
                { n: s.added, l: 'Added', c: C.added, h: 'Nodes carrying text in the later revision only' },
                { n: s.changed, l: 'Changed', c: C.changed, h: 'Same node, different wording' },
                { n: s.removed, l: 'Removed', c: C.removed, h: 'Present in the earlier revision only' },
                { n: s.unchanged, l: 'Unchanged', c: C.same, h: 'Byte-identical — one stored row serves both' },
                { n: `${s.churn_pct}%`, l: 'Churn', c: C.blue, h: `${s.churn} of ${s.total_compared} nodes moved` },
              ].map((k) => (
                <Tooltip key={k.l} title={k.h} placement="top">
                  <Box sx={{ background: C.panel, border: `1px solid ${C.line}`, borderRadius: '8px', p: '14px 16px' }}>
                    <Typography sx={{ fontSize: 26, fontWeight: 700, color: k.c, lineHeight: 1.1, fontFamily: 'monospace' }}>
                      {typeof k.n === 'number' ? k.n.toLocaleString() : k.n}
                    </Typography>
                    <Typography variant="caption" color={C.muted} sx={{ fontSize: 11.5 }}>{k.l}</Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>

            <Box sx={{ mb: '6px' }}>
              <ChurnBar added={s.added} changed={s.changed} removed={s.removed} unchanged={s.unchanged} height={10} />
            </Box>
            <Typography variant="caption" color={C.muted} sx={{ fontSize: 11, display: 'block', mb: '16px' }}>
              {diff.from_revision.name} → {diff.to_revision.name} · {diff.from_revision.nodes} nodes → {diff.to_revision.nodes} nodes ·{' '}
              <span style={{ color: C.dim }}>{s.shared_content_rows.toLocaleString()} content rows shared between them</span>
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 400px) 1fr', gap: '14px', alignItems: 'start' }}>

              {/* ── Per-subchapter ─────────────────────────────────────── */}
              <Panel title="By subchapter" pad="8px">
                {diff.by_subchapter.map((b) => {
                  const selected = scFilter === b.subchapter;
                  return (
                    <Box key={b.subchapter}
                      onClick={() => setScFilter(selected ? 'ALL' : b.subchapter)}
                      sx={{
                        p: '9px 10px', borderRadius: '6px', cursor: 'pointer',
                        background: selected ? '#16202c' : 'transparent',
                        border: `1px solid ${selected ? C.blue : 'transparent'}`,
                        '&:hover': { background: '#141a24' },
                      }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', mb: '5px' }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.text, fontFamily: 'monospace', minWidth: 42 }}>
                          {b.subchapter}
                        </Typography>
                        <Typography variant="caption" color={C.muted} sx={{ fontSize: 10.5 }}>
                          {b.total} nodes
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: 10.5, ml: 'auto', color: b.churn ? C.changed : C.muted }}>
                          {b.churn_pct}%
                        </Typography>
                      </Box>
                      <ChurnBar added={b.added} changed={b.changed} removed={b.removed} unchanged={b.unchanged} />
                      {b.churn > 0 && (
                        <Box sx={{ display: 'flex', gap: '9px', mt: '5px' }}>
                          {b.added > 0 && <Typography variant="caption" sx={{ fontSize: 10, color: C.added }}>+{b.added}</Typography>}
                          {b.changed > 0 && <Typography variant="caption" sx={{ fontSize: 10, color: C.changed }}>~{b.changed}</Typography>}
                          {b.removed > 0 && <Typography variant="caption" sx={{ fontSize: 10, color: C.removed }}>−{b.removed}</Typography>}
                          <Typography variant="caption" sx={{ fontSize: 10, color: C.muted, ml: 'auto', fontFamily: 'monospace' }}>
                            {b.chars_delta > 0 ? '+' : ''}{b.chars_delta.toLocaleString()} ch
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Panel>

              {/* ── Node list ──────────────────────────────────────────── */}
              <Panel
                title={`Changed nodes${scFilter !== 'ALL' ? ` · ${scFilter}` : ''}`}
                pad="0"
                right={
                  <Box sx={{ display: 'flex', gap: '5px' }}>
                    {['ALL', 'CHANGED', 'ADDED', 'REMOVED'].map((k) => (
                      <Chip key={k} label={k === 'ALL' ? `All ${diff.nodes.length}` : KIND[k].label}
                        size="small" onClick={() => setKindFilter(k)}
                        sx={{
                          height: 20, fontSize: 10, cursor: 'pointer', fontWeight: 600,
                          background: kindFilter === k ? (k === 'ALL' ? '#1e293b' : KIND[k].bg) : 'transparent',
                          color: kindFilter === k ? (k === 'ALL' ? C.text : KIND[k].color) : C.muted,
                          border: `1px solid ${kindFilter === k ? (k === 'ALL' ? C.border : KIND[k].color) : C.border}`,
                          '& .MuiChip-label': { px: '7px' },
                        }} />
                    ))}
                  </Box>
                }>
                {visibleNodes.length === 0 ? (
                  <Box sx={{ p: '28px', textAlign: 'center' }}>
                    <Typography variant="body2" color={C.muted} sx={{ fontSize: 13 }}>
                      {diff.nodes.length === 0
                        ? 'These two revisions are identical — every node reads the same text.'
                        : 'Nothing matches this filter.'}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ maxHeight: 620, overflow: 'auto' }}>
                    {visibleNodes.map((n) => {
                      const k = KIND[n.kind];
                      return (
                        <Box key={n.node_id}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            px: '14px', py: '8px', borderBottom: `1px solid ${C.line}`,
                            '&:hover': { background: '#141a24' },
                          }}>
                          <Box sx={{ width: 3, height: 26, background: k.color, borderRadius: '2px', flexShrink: 0 }} />
                          <Chip label={k.label} size="small" sx={{
                            height: 18, fontSize: 9, fontWeight: 700, width: 72, flexShrink: 0,
                            background: k.bg, color: k.color, border: `1px solid ${k.color}44`,
                            '& .MuiChip-label': { px: '5px' },
                          }} />
                          <Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: C.text, fontWeight: 600, minWidth: 0, flex: 1 }}>
                            {n.node_label_path || <span style={{ color: C.muted }}>(no path)</span>}
                          </Typography>
                          <Typography variant="caption" color={C.muted} sx={{ fontSize: 10, width: 82, flexShrink: 0 }}>
                            {n.node_type}
                          </Typography>
                          <Typography variant="caption" sx={{
                            fontSize: 11, fontFamily: 'monospace', width: 92, textAlign: 'right', flexShrink: 0,
                            color: n.chars_delta > 0 ? C.added : n.chars_delta < 0 ? C.removed : C.muted,
                          }}>
                            {n.chars_delta > 0 ? '+' : ''}{(n.chars_delta || 0).toLocaleString()} ch
                          </Typography>
                          <Tooltip title="Show the wording change">
                            <IconButton size="small" onClick={() => openNodeDiff(n)} sx={{ p: '3px', color: C.blue }}>
                              <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      );
                    })}
                    {diff.nodes_truncated && (
                      <Box sx={{ p: '10px 14px' }}>
                        <Typography variant="caption" color={C.muted} sx={{ fontSize: 11 }}>
                          List truncated — narrow it with the subchapter or type filters.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Panel>
            </Box>
          </>
        )}

        {/* ── Word-level node diff ───────────────────────────────────────── */}
        {nodeDiff && (
          <Box sx={{ mt: '14px' }}>
            <Panel
              title={`Wording change · ${nodeDiff.node_label_path || ''}`}
              right={
                <>
                  {nodeDiff.similarity !== undefined && (
                    <Typography variant="caption" color={C.muted} sx={{ fontSize: 10.5 }}>
                      {Math.round(nodeDiff.similarity * 100)}% similar ·{' '}
                      <span style={{ color: C.added }}>+{nodeDiff.words_added}w</span>{' '}
                      <span style={{ color: C.removed }}>−{nodeDiff.words_removed}w</span>
                    </Typography>
                  )}
                  <IconButton size="small" onClick={() => setNodeDiff(null)} sx={{ color: C.muted, p: '2px' }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </>
              }>
              {nodeDiffLoading ? (
                <Box className="flexCenterCenterRow" height="120px"><CircularProgress size={22} sx={{ color: C.blue }} /></Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', gap: '16px', mb: '12px', flexWrap: 'wrap' }}>
                    <Typography variant="caption" color={C.muted} sx={{ fontSize: 11 }}>
                      {diff?.from_revision.name}:{' '}
                      {nodeDiff.before?.present
                        ? `${nodeDiff.before.char_count.toLocaleString()} chars${nodeDiff.before.page_from ? ` · p.${nodeDiff.before.page_from}` : ''}`
                        : 'not present'}
                    </Typography>
                    <Typography variant="caption" color={C.muted} sx={{ fontSize: 11 }}>
                      {diff?.to_revision.name}:{' '}
                      {nodeDiff.after?.present
                        ? `${nodeDiff.after.char_count.toLocaleString()} chars${nodeDiff.after.page_from ? ` · p.${nodeDiff.after.page_from}` : ''}`
                        : 'not present'}
                    </Typography>
                  </Box>
                  <Box sx={{
                    fontFamily: 'monospace', fontSize: 12.5, lineHeight: 1.85,
                    color: C.dim, maxHeight: 460, overflow: 'auto',
                    background: C.sunk, border: `1px solid ${C.line}`, borderRadius: '6px', p: '14px',
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}>
                    {(nodeDiff.segments || []).map((seg, i) => (
                      <Box key={i} component="span" sx={
                        seg.op === 'insert' ? { background: C.addedBg, color: C.added, borderRadius: '2px', px: '2px' }
                        : seg.op === 'delete' ? { background: C.removedBg, color: C.removed, borderRadius: '2px', px: '2px', textDecoration: 'line-through' }
                        : { color: C.dim }
                      }>
                        {seg.text}{' '}
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Panel>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ScRevisionDeltaPage;
